/**
 * Shared plumbing for MarketBetter pages that run inside a Salesforce iframe
 * (`/salesforce-cti`, `/salesforce-account`).
 *
 * These pages have no MarketBetter login: third-party storage in the iframe
 * never carries the app session. The Account panel is signed in by a Canvas
 * signed request that the backend verifies (see `exchangeCanvasHandoff`) and
 * receives a short-lived, read-only API token held in frame memory only.
 */

/** Route prefixes that only ever render inside a Salesforce iframe. */
const SALESFORCE_EMBED_ROUTES = ['/salesforce-account', '/salesforce-cti'] as const;

/**
 * True when this frame is one of the Salesforce embed surfaces.
 *
 * Such a frame must never reload itself. Its credential comes from a Canvas
 * signed request that only Salesforce can produce (the single-use handoff code
 * lives in memory and is stripped from the address bar), so a reloaded frame
 * comes back with nothing to sign in with. Every automatic reload path
 * (build-version polling, deploy-error recovery) checks this first and leaves
 * the frame alone; the user reloads the Salesforce page instead.
 */
export const isSalesforceEmbedFrame = (
  pathname: string = typeof window === 'undefined' ? '' : window.location.pathname,
): boolean => SALESFORCE_EMBED_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

/**
 * The session the backend mints for one embedded frame. Held in memory by
 * `utils/requestAuth.ts` (see there for why neither localStorage nor
 * sessionStorage is acceptable).
 */
export interface SalesforceEmbedSession {
  accessToken: string;
  userId: string;
  vendorId: number;
  /** ISO timestamp; the token is not sent once it has passed. */
  expiresAt: string;
}

/**
 * Origin the public integration endpoints live under, keyed off where the page
 * is served. Callers append `/api/public/...`, so this never ends in `/api`.
 *
 * Production and dev both proxy `/api/` to the backend on the page's own
 * origin (`marketbetter.ai`, `www.marketbetter.ai`, `dev.marketbetter.ai`).
 * The previous version returned `https://www.marketbetter.ai/api` for
 * production, so the CTI softphone and the Account panel posted to
 * `/api/api/public/...` and got an Express 404 there.
 */
export const getSalesforceEmbedApiBaseUrl = (
  location: { hostname: string; origin: string } | undefined = typeof window === 'undefined' ? undefined : window.location,
): string => {
  if (!location) return '';
  const { hostname } = location;

  // Local development: frontend on 3330, backend on 8881.
  if (hostname === 'localhost' || hostname.startsWith('10.13.') || hostname.startsWith('192.168.')) {
    const backendHost = hostname === 'localhost' ? 'localhost' : hostname;
    return `http://${backendHost}:8881`;
  }
  return location.origin;
};

/**
 * How the panel's company domain was found. `email_domain`: a Lead whose Website was
 * missing or a personal mailbox domain, matched on the Lead's business email instead.
 */
export type SalesforceAccountResolution = 'matched' | 'no_website' | 'personal_domain' | 'email_domain';

export interface SalesforceEmbedAccount {
  id: string;
  name: string | null;
  website: string | null;
  /** Normalised the way Account Priority keys accounts; null when the record has no usable website. */
  domain: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  industry: string | null;
  billingCountry: string | null;
  lastModifiedAt: string | null;
  url: string;
}

/** The Lead the panel was opened on. Queue-owned Leads carry the queue name and no owner email. */
export interface SalesforceEmbedLead {
  id: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  title: string | null;
  company: string | null;
  status: string | null;
  leadSource: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  isConverted: boolean;
  convertedAccountId: string | null;
  url: string;
}

export type SalesforceEmbedRecordType = 'account' | 'lead';

/** An open Opportunity on an existing Account, as the viewer can see it in Salesforce. */
export interface SalesforceEmbedOpportunity {
  id: string;
  name: string | null;
  amount: number | null;
  /** The amount in the deal's currency and the viewer's locale, from Salesforce's FORMAT(). */
  amountFormatted: string | null;
  stage: string | null;
  closeDate: string | null;
  url: string;
}

/**
 * An existing Account with the panel record's domain. On a Lead it also says who owns it and
 * which deals are open, so a rep sees that a colleague already works the company. Those fields
 * are absent on an Account page and from older backends; `openOpportunities` is null when the
 * viewer cannot read Opportunities (unknown, not "no deals").
 */
export interface SalesforceSharedDomainAccount {
  id: string;
  name: string | null;
  url: string;
  ownerId?: string | null;
  ownerName?: string | null;
  /** null when the viewer is unknown. */
  ownedByViewer?: boolean | null;
  openOpportunityCount?: number | null;
  /** The largest open deals first, at most 3. */
  openOpportunities?: SalesforceEmbedOpportunity[] | null;
}

export interface SalesforceAccountInitResponse {
  success: true;
  vendorId: number;
  marketbetterUserId: string;
  accessToken: string;
  expiresInMinutes: number;
  viewer:
    | { source: 'connector' | 'connector_member'; verified: false }
    | { source: 'canvas'; verified: true; email: string; salesforceUserId: string | null };
  /**
   * The Salesforce record. On a Lead this is the Lead's company: `id` is the Lead Id,
   * `name` the Company, `domain` from Website or else the business email's domain.
   */
  account: SalesforceEmbedAccount;
  resolution: SalesforceAccountResolution;
  /**
   * On an Account: other Accounts with the same domain. On a Lead: existing Accounts for
   * the Lead's company. Empty when the viewer cannot read Accounts.
   */
  sharedDomainAccounts: SalesforceSharedDomainAccount[];
  /** Absent from a backend that predates Lead support: treat as an Account. */
  recordType?: SalesforceEmbedRecordType;
  lead?: SalesforceEmbedLead | null;
}

/** The Lead this session was opened on, or null for an Account (or an older backend). */
export const sessionLead = (session: Pick<SalesforceAccountInitResponse, 'recordType' | 'lead'>): SalesforceEmbedLead | null => (
  session.recordType === 'lead' && session.lead ? session.lead : null
);

export type SalesforceEmbedErrorCode =
  | 'canvas_required'
  | 'invalid_handoff'
  | 'canvas_expired'
  | 'invalid_signed_request'
  | 'invalid_signature'
  | 'stale_signed_request'
  | 'missing_instance'
  | 'not_connected'
  | 'ambiguous'
  | 'token_expired'
  | 'not_member'
  | 'invalid_account_id'
  | 'invalid_record_id'
  | 'account_not_found'
  | 'internal_error'
  | 'network';

export class SalesforceEmbedInitError extends Error {
  code: SalesforceEmbedErrorCode;

  status: number | null;

  constructor(code: SalesforceEmbedErrorCode, message: string, status: number | null = null) {
    super(message);
    this.name = 'SalesforceEmbedInitError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Inside the Salesforce iframe, links into the rest of the app (the shared
 * account body renders "Open drafts in Tasks", the roster links to people)
 * must not navigate the iframe: the frame only holds a read-only embed
 * credential that cannot authenticate /tasks. Open them as a normal app tab
 * instead, where the user's own login applies. Attach as a capture-phase click
 * handler on the panel root.
 */
export const openAppLinksInNewTab = (
  event: { target: EventTarget | null; defaultPrevented: boolean; preventDefault: () => void },
  origin: string = typeof window === 'undefined' ? '' : window.location.origin,
  open: (url: string) => void = (url) => { window.open(url, '_blank', 'noopener'); },
): boolean => {
  if (event.defaultPrevented) return false;
  const target = event.target as Element | null;
  const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!anchor) return false;
  const href = anchor.getAttribute('href') || '';
  if (!href || href.startsWith('#') || anchor.target === '_blank') return false;
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return false;
  }
  if (url.origin !== origin) return false;
  if (url.pathname.startsWith('/salesforce-account')) return false;
  event.preventDefault();
  open(url.toString());
  return true;
};

/**
 * The Canvas route handler (`/salesforce-account/canvas`) hands the page a
 * single-use handoff code in the URL fragment (`#canvasCode=…`), or
 * `#canvasError=<code>&canvasMessage=<text>` when verification failed. The
 * fragment is read once and removed from the address bar; the code is then
 * redeemed for the session with `exchangeCanvasHandoff`. A code is never a
 * bearer: it is consumed atomically on first use and dies after 60 seconds.
 */
export type CanvasHandoff =
  | { kind: 'code'; code: string }
  | { kind: 'error'; code: SalesforceEmbedErrorCode; message: string }
  | null;

/** Where the inline stash script in the segment layout parks the fragment before hydration. */
declare global {
  interface Window { __mbCanvasHandoff?: string }
}

/**
 * Redact a Canvas handoff from any URL that leaves the browser (Datadog RUM view and
 * resource URLs, Logs `view.url`). The inline layout script strips the fragment before
 * hydration, but the navigation PerformanceEntry and the very first log line still see
 * the original address, so telemetry must never carry the code.
 */
export const scrubCanvasHandoffFromUrl = (rawUrl: string): string => {
  if (!rawUrl || !/canvasCode=/.test(rawUrl)) return rawUrl;
  return rawUrl.replace(/canvasCode=[^&#]*/g, 'canvasCode=REDACTED');
};

/**
 * The stash wins over the live hash: the layout script moves the fragment there before
 * React runs. Reading is idempotent — React evaluates a `useRef(init)` argument on every
 * render and a hydration retry re-runs the first render — so the stash is never consumed
 * here. It is memory-only and dies with the frame.
 */
const canvasFragment = (): string => {
  if (typeof window === 'undefined') return '';
  return window.__mbCanvasHandoff || window.location.hash;
};

export const readCanvasHandoff = (
  hash: string = canvasFragment(),
): CanvasHandoff => {
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.slice(1));
  const code = params.get('canvasCode');
  if (code) {
    return code.length >= 20 ? { kind: 'code', code } : { kind: 'error', code: 'invalid_handoff', message: 'Malformed Canvas handoff.' };
  }
  const error = params.get('canvasError');
  if (error) {
    return { kind: 'error', code: error as SalesforceEmbedErrorCode, message: params.get('canvasMessage') || 'Canvas verification failed.' };
  }
  return null;
};

export const clearCanvasHandoff = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // non-fatal
  }
};

/** Redeem a handoff code for the verified session. Exactly once; a replay gets `invalid_handoff`. */
export const exchangeCanvasHandoff = async (
  handoffCode: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SalesforceAccountInitResponse> => {
  let response: Response;
  try {
    response = await fetchImpl(`${getSalesforceEmbedApiBaseUrl()}/api/public/integration/salesforce-canvas-exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoffCode }),
    });
  } catch (error) {
    throw new SalesforceEmbedInitError('network', (error as Error).message || 'Could not reach MarketBetter');
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new SalesforceEmbedInitError(
      (body?.code as SalesforceEmbedErrorCode) || 'internal_error',
      body?.message || body?.error || `MarketBetter returned ${response.status}`,
      response.status,
    );
  }
  return body as SalesforceAccountInitResponse;
};
